import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import * as React from 'react';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { handleUnauthorizedResponse } from '../../../api/http';
import {
	AuthData,
	UI_HIDE_INACTIVE_ENTITIES_ENABLED,
} from '../../../context/auth';
import { PolicyData, withPolicyContext } from '../../../context/policy';
import { Peril, PerilCharacteristics } from '../../../types/policy';
import { tableStyles } from '../../../util/styles';
import {
	getComparator,
	HeadCell,
	Order,
	stableSort,
} from '../../../util/tableUtil';
import {
	getActiveCharacteristics,
	getAddedAndRemovedPerilsFromExpsosure,
	getEntityNameFrom_DescriptionField,
	getExposureObject,
	isPerilActive,
	isPolicyEditable,
	sortCharacteristicsByCoverageStartDate,
} from '../../../util/util';
import { StyledEmptyMessage, StyledTableActions } from '../../common';
import SortableTable from '../../SortableTable';
import { errorToast, successToast } from '../../Toast';
import ConfirmationModal from '../ConfirmationModal';

interface Props {
	perils: Peril[];
	authData: AuthData;
	policyData: PolicyData;
}

interface Data {
	perilId: string;
	perilName: string;
	dateCreated: string;
	actions: any;
	link: string;
	isActive: boolean;
}

function createData(
	perilId: string,
	perilName: string,
	dateCreated: string,
	actions: any,
	isActive: boolean,
	link: string,
): Data {
	return { perilId, perilName, dateCreated, actions, isActive, link };
}

const PerilsList = (props: Props) => {
	const navigate = useNavigate();
	const { t } = useTranslation();
	const { getValues, formState, reset } = useFormContext();
	const { dirtyFields } = formState;
	const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
	const [selectedPeril, setSelectedPeril] = useState(null);
	const [order, setOrder] = React.useState<Order>('asc');
	const [orderBy, setOrderBy] = React.useState<string>('dateCreated');
	const { perils, policyData, authData } = props;
	const hideInactiveEntities =
		authData?.auth?.configProperties?.[UI_HIDE_INACTIVE_ENTITIES_ENABLED];
	const {
		quoteUpdateObj,
		policyUpdateObj,
		policy,
		selectedQuote,
		selectedExposure,
		productConfig,
		policyViewTimestamp,
		saveFastForm,
		setLoading,
	} = policyData;
	let updateObject = selectedQuote ? quoteUpdateObj : policyUpdateObj;
	const params = useParams();
	const currentPerils = perils as any;
	const exposureObj: any = getExposureObject(
		updateObject,
		selectedExposure.locator,
	);

	const classes = tableStyles();

	if (!exposureObj) {
		navigate(`/policy/${params.policyId}/quotes/${params.quoteId}/`);
	}

	const exposureName =
		selectedExposure.name || (selectedExposure as any).exposureName;
	const exposureConfig = productConfig.policyConfiguration.exposures.find(
		(e) => e.name === exposureName,
	);
	const perilConfigs = exposureConfig.perils;
	const isPolicyIssued = !!policy.issuedTimestamp;
	const newOrRemovedPerils = getAddedAndRemovedPerilsFromExpsosure(exposureObj);
	const combinedPerils = newOrRemovedPerils[0]
		? currentPerils.concat(newOrRemovedPerils[0])
		: currentPerils;
	const filteredPerils = combinedPerils.filter(
		(p: any) => !newOrRemovedPerils[1].includes(p.locator),
	);
	const isInEditMode =
		authData.hasPermission('quote.update') &&
		isPolicyEditable(policy, selectedQuote);

	const emptyTable = !filteredPerils.length ? (
		<StyledEmptyMessage>No {t('peril.plural.lower')}</StyledEmptyMessage>
	) : null;

	const headCells: HeadCell[] = [
		{
			id: 'perilName',
			rightAlign: false,
			label: `${t('peril.singular.upper')} Name`,
		},
		{ id: 'dateCreated', rightAlign: false, label: 'Date Created' },
	];

	if (isPolicyIssued) {
		headCells.push({ id: 'isActive', rightAlign: false, label: 'Status' });
	}

	if (isInEditMode) {
		headCells.push({ id: 'actions', rightAlign: false, label: 'Actions' });
	}

	const removePeril = async () => {
		setIsConfirmModalOpen(false);
		if (!selectedPeril) {
			return;
		} else {
			setLoading(true);
			try {
				await saveFastForm(getValues(), dirtyFields, reset, {
					removePeril: {
						exposureLocator: selectedExposure.locator,
						perilLocator: selectedPeril.locator,
					},
				});
				successToast('Successfully removed peril');
			} catch (error) {
				errorToast('Error removing peril');
				if (await handleUnauthorizedResponse(error, authData)) {
					return;
				}
			} finally {
				setLoading(false);
			}
		}
	};

	const renderRemovePerilModal = () => {
		return (
			<ConfirmationModal
				entityName={selectedPeril.name}
				entityType={t('peril.singular.lower')}
				authToken={authData.auth.authorizationToken}
				onRemove={removePeril}
				cancel={() => setIsConfirmModalOpen(false)}
			/>
		);
	};

	const rows: any = filteredPerils
		.filter((peril: any) => {
			const latestActivePerilCharacteristic =
				sortCharacteristicsByCoverageStartDate(
					getActiveCharacteristics(
						peril.characteristics,
					) as PerilCharacteristics[],
				).pop();
			return !!latestActivePerilCharacteristic;
		})
		.map((peril: any, index: number) => {
			const perilName = peril.name;
			const perilConfig = perilConfigs.find((p) => p.name === perilName);
			const latestActivePerilCharacteristic =
				sortCharacteristicsByCoverageStartDate(
					getActiveCharacteristics(
						peril.characteristics,
					) as PerilCharacteristics[],
				).pop();
			const displayName =
				getEntityNameFrom_DescriptionField(latestActivePerilCharacteristic) ||
				perilConfig.displayName;
			const newPerilIndex = index - perils.length;
			const urlId = peril.displayId
				? peril.displayId
				: `added-${perilName}-${newPerilIndex}`;
			const displayId = peril.displayId ? peril.displayId : 'N/A';
			const createdTimestamp = peril.createdTimestamp
				? peril.createdTimestamp
				: Date.now();
			const perilDetailsLink =
				!selectedQuote || policy.issuedTimestamp
					? `exposures/${selectedExposure.displayId}/perils/${urlId}`
					: `quotes/${selectedQuote.locator}/exposures/${selectedExposure.displayId}/perils/${urlId}`;
			const isActive = isPerilActive(
				peril,
				selectedExposure,
				policyViewTimestamp,
			);
			const onRemovePeril = (e: any) => {
				e.stopPropagation();
				e.preventDefault();
				setSelectedPeril(peril);
				setIsConfirmModalOpen(true);
			};

			const actions = (
				<StyledTableActions>
					{isInEditMode && <a onClick={onRemovePeril}>Remove</a>}
				</StyledTableActions>
			);
			return createData(
				displayId,
				displayName,
				createdTimestamp,
				actions,
				isActive,
				`/policy/${policy.locator}/${perilDetailsLink}`,
			);
		});

	const rowElements = stableSort(rows, getComparator(order, orderBy)).map(
		(row: any, index: number) => {
			if (!row.isActive && hideInactiveEntities) {
				return null;
			}
			return (
				<TableRow
					hover
					tabIndex={-1}
					key={index}
					component={Link}
					to={row.link}
					className={classes.tableRow}
				>
					<TableCell>{row.perilName}</TableCell>
					<TableCell>{authData.getFormattedDate(row.dateCreated)}</TableCell>
					{isPolicyIssued && (
						<TableCell>{row.isActive ? 'Active' : 'Inactive'}</TableCell>
					)}
					{isInEditMode && <TableCell>{row.actions}</TableCell>}
				</TableRow>
			);
		},
	);

	return (
		<>
			{isConfirmModalOpen && renderRemovePerilModal()}
			<SortableTable
				rows={rowElements}
				headCells={headCells}
				additionalMsg={emptyTable}
				order={order}
				setOrder={setOrder}
				orderBy={orderBy}
				setOrderBy={setOrderBy}
			/>
		</>
	);
};

export default withPolicyContext<Props>(PerilsList);
